import type { Region, Figure, NameIndexEntry } from '../types'
import { normalizeQuery } from './format'
import regionsData from '../data/regions.json'
import figuresData from '../data/figures.json'

const regions = regionsData as Region[]
const figures = figuresData as Figure[]

// =============================================================================
// Common modern / anglicised spellings for figures whose Sanskrit IAST doesn't
// produce a familiar ASCII form when diacritics are merely stripped. Vocalic
// ṛ in particular ("Kṛṣṇa") collapses to bare "r" via NFD, so a user typing
// "Krishna" wouldn't otherwise find Vāsudeva Kṛṣṇa. We also catch conventional
// English-press spellings ("Ashoka", "Shankara", "Brihadratha") that differ
// from strict IAST stripping ("Asoka", "Sankara", "Brhadratha").
// =============================================================================
const FIGURE_MODERN_ALIASES: Record<string, string[]> = {
  // Vedic / Puranic — anglicised modern forms
  manu:                ['Manu'],
  yayati:              ['Yayati'],
  puru:                ['Puru'],
  yadu:                ['Yadu'],
  mandhatri:           ['Mandhata', 'Mandhatri'],
  kartavirya:          ['Kartavirya Arjuna', 'Sahasrabahu Arjuna', 'Sahasrarjuna'],
  sagara:              ['Sagara'],
  rama:                ['Rama', 'Ram', 'Ramachandra', 'Sri Ram', 'Lord Ram'],
  vyasa:               ['Vyasa', 'Veda Vyasa', 'Krishna Dwaipayana'],
  vasudeva:            ['Krishna', 'Krishna Vasudeva', 'Sri Krishna', 'Lord Krishna', 'Krsna'],
  arjuna:              ['Arjuna'],
  duryodhana:          ['Duryodhana'],
  parikshit:           ['Parikshit', 'Parikshita'],
  janamejaya:          ['Janamejaya'],
  jarasandha:          ['Jarasandha'],
  brihadratha:         ['Brihadratha'],
  sudas:               ['Sudas', 'Sudasa'],
  vasishtha:           ['Vasishtha', 'Vashishtha', 'Vashishta', 'Vasishta'],
  visvamitra:          ['Vishwamitra', 'Vishvamitra', 'Visvamitra'],
  bharadvaja:          ['Bharadwaja', 'Bharadvaja', 'Bharadwaj'],
  atri:                ['Atri'],
  kanva:               ['Kanva'],
  gritsamada:          ['Gritsamada'],
  vamadeva:            ['Vamadeva'],
  janaka:              ['Janaka', 'Raja Janaka', 'Janak'],
  yajnavalkya:         ['Yajnavalkya', 'Yagnavalkya'],
  maitreyi:            ['Maitreyi'],
  gargi:               ['Gargi', 'Gargi Vachaknavi'],
  pravahana:           ['Pravahana', 'Pravahana Jaivali'],
  uddalaka:            ['Uddalaka', 'Aruni', 'Uddalaka Aruni'],
  svetaketu:           ['Shvetaketu', 'Svetaketu'],
  asvapati:            ['Ashvapati', 'Aswapati', 'Asvapati Kaikeya'],
  ajatasatru_kashi:    ['Ajatashatru of Kashi', 'Ajatasatru of Kashi', 'Ajatasatru Kashya'],
  sanatkumara:         ['Sanatkumara', 'Sanat Kumara'],
  // Imperial Unity → Classical → Kanauj
  buddha:              ['Buddha', 'Gautama Buddha', 'Siddhartha', 'Siddhartha Gautama', 'Shakyamuni', 'Sakyamuni'],
  mahavira:            ['Mahavira', 'Vardhamana', 'Vardhamana Mahavira'],
  chanakya:            ['Chanakya', 'Kautilya', 'Vishnugupta'],
  chandragupta_maurya: ['Chandragupta', 'Chandragupta Maurya'],
  ashoka:              ['Ashoka', 'Ashoka the Great', 'Ashoka Maurya', 'Asoka'],
  kharavela:           ['Kharavela', 'Kharavela of Kalinga'],
  kaniska:             ['Kanishka', 'Kanishka the Great', 'Kaniska'],
  rudradaman:          ['Rudradaman'],
  samudragupta:        ['Samudragupta'],
  chandragupta_ii:     ['Chandragupta II', 'Vikramaditya', 'Chandragupta Vikramaditya'],
  kalidasa:            ['Kalidasa'],
  aryabhata:           ['Aryabhata', 'Aryabhatta'],
  harsha:              ['Harsha', 'Harshavardhana', 'Harsha of Kanauj'],
  pulakeshin_ii:       ['Pulakeshin II', 'Pulakesin II', 'Pulakeshin'],
  shankara:            ['Shankara', 'Shankaracharya', 'Adi Shankara', 'Adi Shankaracharya', 'Sankara'],
  rajaraja_chola:      ['Rajaraja', 'Rajaraja I', 'Rajaraja Chola'],
  rajendra_chola:      ['Rajendra', 'Rajendra I', 'Rajendra Chola'],
  ramanuja:            ['Ramanuja', 'Ramanujacharya'],
  alauddin_khalji:     ['Alauddin Khilji', 'Alauddin Khalji'],
  krishna_deva_raya:   ['Krishnadevaraya', 'Krishna Deva Raya'],
  kabir:               ['Kabir', 'Sant Kabir', 'Kabir Das'],
  guru_nanak:          ['Guru Nanak', 'Nanak', 'Nanak Dev'],
  akbar:               ['Akbar', 'Akbar the Great', 'Jalaluddin Akbar'],
  shivaji:             ['Shivaji', 'Chhatrapati Shivaji', 'Shivaji Maharaj'],
  aurangzeb:           ['Aurangzeb', 'Alamgir'],
  ranjit_singh:        ['Ranjit Singh', 'Maharaja Ranjit Singh', 'Sher-e-Punjab'],
  ram_mohan_roy:       ['Ram Mohan Roy', 'Raja Ram Mohan Roy', 'Rammohan Roy'],
  vivekananda:         ['Vivekananda', 'Swami Vivekananda', 'Narendranath'],
  tilak:               ['Tilak', 'Bal Gangadhar Tilak', 'Lokmanya Tilak'],
  gandhi:              ['Gandhi', 'Mahatma Gandhi', 'Mohandas Gandhi', 'Bapu', 'Mohandas Karamchand Gandhi']
}

export function buildNameIndex(): NameIndexEntry[] {
  const out: NameIndexEntry[] = []

  // --- Regions: every primaryName + every alias ---
  for (const r of regions) {
    out.push({
      q: normalizeQuery(r.primaryName),
      display: r.primaryNameIAST ?? r.primaryName,
      regionId: r.id,
      kind: 'historical-region'
    })
    for (const alias of r.aliases ?? []) {
      const q = normalizeQuery(alias)
      if (!q) continue
      const lowerAlias = alias.toLowerCase()
      let kind: NameIndexEntry['kind'] = 'historical-region'
      const modernHints = r.modernEquivalent.map(m => m.toLowerCase())
      if (modernHints.some(m => m.includes(lowerAlias)) ||
          /district|presidency|province|state|valley/.test(lowerAlias)) {
        kind = 'modern-region'
      } else if (lowerAlias.includes('pura') || lowerAlias.includes('nagar')) {
        kind = 'historical-city'
      } else if (/^(patna|delhi|lucknow|mumbai|hyderabad|kolkata|calcutta|madras|chennai|jaipur|pune|ahmedabad|bangalore|bengaluru|guwahati|peshawar|kabul|dhaka|lahore|colombo|kandy)$/i.test(alias)) {
        kind = 'modern-city'
      }
      out.push({ q, display: alias, regionId: r.id, kind })
    }
  }

  // --- Figures: name + IAST + modern aliases ---
  for (const f of figures) {
    const display = f.nameIAST ?? f.name
    // Primary: the IAST name (this is what's displayed everywhere else).
    out.push({ q: normalizeQuery(f.name), display, figureId: f.id, kind: 'figure' })
    if (f.nameIAST && normalizeQuery(f.nameIAST) !== normalizeQuery(f.name)) {
      out.push({ q: normalizeQuery(f.nameIAST), display, figureId: f.id, kind: 'figure' })
    }
    // Modern / anglicised aliases (Krishna for Kṛṣṇa, Ashoka for Aśoka, etc.).
    for (const alias of FIGURE_MODERN_ALIASES[f.id] ?? []) {
      const q = normalizeQuery(alias)
      if (!q) continue
      out.push({ q, display, figureId: f.id, kind: 'figure' })
    }
  }

  // De-duplicate (q, regionId, figureId) tuples, preferring first occurrence.
  const seen = new Set<string>()
  return out.filter(e => {
    const key = e.q + '|' + (e.regionId ?? '') + '|' + (e.figureId ?? '')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const nameIndex: NameIndexEntry[] = buildNameIndex()

export function searchNames(query: string, max = 8): NameIndexEntry[] {
  const q = normalizeQuery(query)
  if (!q) return []
  // Score: exact match > prefix > substring. Slight bonus to figures so that
  // a user typing "Krishna" sees Vāsudeva Kṛṣṇa before any region containing
  // the substring elsewhere (none today, but defensive).
  const scored: { entry: NameIndexEntry; score: number }[] = []
  for (const e of nameIndex) {
    let score = 0
    if (e.q === q) score = 100
    else if (e.q.startsWith(q)) score = 60
    else if (e.q.includes(q)) score = 30
    if (score > 0) scored.push({ entry: e, score })
  }
  scored.sort((a, b) => b.score - a.score)
  return scored.slice(0, max).map(s => s.entry)
}
